/**
 * ICE candidate filtering for WHEP answers.
 *
 * ★★ THIS IS A SECOND COPY. The original is
 *    `clients/web-cockpit/src/lib/iceFilter.ts` and the two must stay in sync —
 *    `0xman/deploy/check_ice_filter_copies.sh` fails the build if they diverge.
 *    Why a copy at all: the cockpit is embedded in the dog's binary and the
 *    console is a separate cloud SPA; there is no shared package between the two
 *    vite apps, and adding one would mean touching the cockpit's build config —
 *    a much bigger blast radius than a guarded copy for the dog's critical path.
 *
 * WHY THIS FILE EXISTS AT ALL: go2rtc reports EVERY local interface as a
 * candidate. Ones this browser cannot reach only stretch the ICE timeout out.
 *
 * ⚠ Two production incidents live in here, and both had the same signature —
 *   "WHEP negotiated fine (201), picture stays black":
 *
 *   2026-07-28  The filter was "keep RFC1918 only". Once the dog moved onto the
 *               private net its only externally reachable address was
 *               100.64.0.2 — and 100.64/10 is CGNAT, NOT RFC1918 — so the one
 *               candidate that worked got dropped. All three cameras black,
 *               while go2rtc's own player (no filter) played 1280x720 fine.
 *
 *   2026-08-01  Video fan-out moved the media endpoint to the relay on panda02,
 *               whose only browser-reachable candidate from the public internet
 *               is 119.45.128.152 — a PUBLIC address, neither RFC1918 nor CGNAT,
 *               so the same filter dropped it too. Same black picture.
 *
 * The fix was NOT "add one more subnet to the allowlist" (there is always a next
 * subnet). It is the rule this file states at the top and must execute to the
 * end: **the address this page was loaded from is, by construction, reachable
 * from this browser.** Provenance is the criterion; subnets are the supplement.
 */

const RFC1918 = /192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+/;
const CGNAT = /100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.\d+\.\d+/; // 100.64/10 private net (tailnet)
const LOOPBACK = /127\.\d+\.\d+\.\d+|::1/;

/** Was this page itself loaded from a PUBLIC address? Then public candidates are reachable. */
function selfIsPublic(selfHost?: string): boolean {
  if (!selfHost) return false;
  const h = selfHost.replace(/^\[|\]$/g, '');
  if (h === 'localhost') return false;
  return !RFC1918.test(h) && !CGNAT.test(h) && !LOOPBACK.test(h);
}

/** Is this candidate a public address (i.e. not a private range and not loopback)? */
function isPublicCandidate(line: string): boolean {
  const m = line.match(/\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/);
  if (!m) return false;
  const ip = m[1];
  return !RFC1918.test(ip) && !CGNAT.test(ip) && !LOOPBACK.test(ip) && !/^169\.254\./.test(ip);
}

export function candidateReachable(line: string, selfHost?: string): boolean {
  if (selfHost && line.includes(` ${selfHost} `)) return true;
  if (selfIsPublic(selfHost) && isPublicCandidate(line)) return true;
  return RFC1918.test(line) || CGNAT.test(line);
}

/** Only `a=candidate:` lines are touched; everything else is passed through verbatim. */
export function filterIceCandidates(sdp: string, selfHost?: string): string {
  const lines = sdp.split('\n');
  const kept = lines.filter(l => !l.startsWith('a=candidate:') || candidateReachable(l, selfHost));
  // Escape hatch (CLAUDE.md §10): if nothing survived, the connection is doomed by
  // construction. Hand the browser the untouched answer and let it try them all
  // (slow) rather than a filtered one that cannot possibly connect (dead).
  if (!kept.some(l => l.startsWith('a=candidate:'))) return sdp;
  return kept.join('\n');
}
